import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { getAmenities, getMyBookings, createBooking, updateBookingStatus } from '@/services/api';
import { Calendar, Dumbbell, Waves, Building2, Trophy, Clock, CheckCircle2, XCircle, AlertCircle, X, IndianRupee, Ticket, Copy } from 'lucide-react';
import { toast } from 'sonner';

const ResidentAmenities = () => {
  const [amenities, setAmenities] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAmenity, setSelectedAmenity] = useState(null);
  const [bookingData, setBookingData] = useState({
    date: '',
    time_slot: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [amenitiesData, bookingsData] = await Promise.all([
        getAmenities(),
        getMyBookings()
      ]);
      setAmenities(amenitiesData);
      setBookings(bookingsData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!selectedAmenity) return;

    try {
      await createBooking({
        amenity_id: selectedAmenity.id,
        amenity_name: selectedAmenity.name,
        date: bookingData.date,
        time_slot: bookingData.time_slot
      });
      toast.success('Amenity booked successfully');
      setDialogOpen(false);
      setBookingData({ date: '', time_slot: '' });
      setSelectedAmenity(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to book amenity');
    }
  };

  const getAmenityIcon = (type) => {
    const icons = {
      gym: Dumbbell,
      pool: Waves,
      clubhouse: Building2,
      court: Trophy
    };
    return icons[type] || Calendar;
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    
    try {
      await updateBookingStatus(bookingId, 'cancelled');
      toast.success('Booking cancelled successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const getStatusBadge = (status, date) => {
    const statusConfig = {
      confirmed: { icon: Clock, color: 'bg-blue-100 text-blue-700', label: 'Confirmed' },
      used: { icon: CheckCircle2, color: 'bg-green-100 text-green-700', label: 'Checked In' },
      checked_out: { icon: CheckCircle2, color: 'bg-teal-100 text-teal-700', label: 'Checked Out' },
      expired: { icon: AlertCircle, color: 'bg-slate-100 text-slate-500', label: 'Expired' },
      cancelled: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Cancelled' },
      no_show: { icon: XCircle, color: 'bg-orange-100 text-orange-700', label: 'No Show' }
    };
    const config = statusConfig[status] || statusConfig.confirmed;
    const Icon = config.icon;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-1 ${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  const isUpcoming = (date) => {
    const bookingDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bookingDate >= today;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="resident-amenities">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Amenities</h1>
        <p className="text-slate-600">Book society amenities</p>
      </div>

      {/* Available Amenities */}
      <div>
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Available Amenities</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {amenities.map((amenity) => {
            const Icon = getAmenityIcon(amenity.type);
            return (
              <Card key={amenity.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="amenity-item">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="h-6 w-6 text-secondary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-900 mb-1">{amenity.name}</h3>
                      <p className="text-sm text-slate-600 mb-3">{amenity.description}</p>
                      {/* Price Badge */}
                      <div className="mb-3">
                        <Badge className={amenity.price > 0 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                          <IndianRupee className="h-3 w-3 mr-1" />
                          {amenity.price > 0 ? `${amenity.price.toFixed(2)} per booking` : 'Free'}
                        </Badge>
                      </div>
                      <div className="mb-3">
                        <p className="text-xs text-slate-500 mb-1">Available Slots:</p>
                        <div className="flex flex-wrap gap-2">
                          {amenity.available_slots.map((slot, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 bg-slate-100 rounded-md">{slot}</span>
                          ))}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="bg-secondary hover:bg-secondary/90"
                        onClick={() => {
                          setSelectedAmenity(amenity);
                          setDialogOpen(true);
                        }}
                        data-testid="book-amenity-btn"
                      >
                        Book Now
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* My Upcoming Bookings */}
      <div>
        <h2 className="text-xl font-semibold text-slate-900 mb-4">My Upcoming Bookings</h2>
        <div className="grid gap-4">
          {bookings.filter(b => b.status === 'confirmed' || b.status === 'pending').length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center">
                <p className="text-slate-500">No upcoming bookings</p>
              </CardContent>
            </Card>
          ) : (
            bookings.filter(b => b.status === 'confirmed' || b.status === 'pending').map((booking) => (
              <Card key={booking.id} className="border-slate-200" data-testid="booking-item">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900">{booking.amenity_name}</h3>
                        {booking.booking_code && (
                          <Badge 
                            variant="outline" 
                            className="font-mono bg-secondary/10 text-secondary border-secondary/30 cursor-pointer hover:bg-secondary/20"
                            onClick={() => {
                              try {
                                if (navigator.clipboard?.writeText) {
                                  navigator.clipboard.writeText(booking.booking_code).then(() => toast.success('Booking code copied!')).catch(() => toast.info('Code: ' + booking.booking_code));
                                } else {
                                  const ta = document.createElement('textarea'); ta.value = booking.booking_code; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
                                  toast.success('Booking code copied!');
                                }
                              } catch { toast.info('Code: ' + booking.booking_code); }
                            }}
                            data-testid="booking-code-badge"
                          >
                            <Ticket className="h-3 w-3 mr-1" />
                            {booking.booking_code}
                            <Copy className="h-3 w-3 ml-1" />
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">
                        <span className="font-medium">Date:</span> {booking.date} | <span className="font-medium">Time:</span> {booking.time_slot}
                      </p>
                      {booking.booking_code && (
                        <p className="text-xs text-slate-500 mt-1">
                          Show this code to security for check-in
                        </p>
                      )}
                      {booking.payment_status === 'pending' && booking.status === 'confirmed' && (
                        <p className="text-xs text-red-600 font-medium mt-1">
                          Payment pending — pay the amenity bill before check-in
                        </p>
                      )}
                      {booking.payment_status === 'paid' && booking.status === 'confirmed' && (
                        <p className="text-xs text-green-600 font-medium mt-1">
                          Payment complete — ready for check-in
                        </p>
                      )}
                      {booking.checked_in_at && (
                        <p className="text-xs text-green-600 mt-1">
                          Checked in at {new Date(booking.checked_in_at).toLocaleTimeString()} by {booking.checked_in_by}
                        </p>
                      )}
                      {booking.checked_out_at && (
                        <p className="text-xs text-teal-600 mt-1">
                          Checked out at {new Date(booking.checked_out_at).toLocaleTimeString()} by {booking.checked_out_by}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(booking.status, booking.date)}
                      {booking.status === 'confirmed' && isUpcoming(booking.date) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancelBooking(booking.id)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          data-testid="cancel-booking-btn"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Past Bookings */}
      {bookings.filter(b => b.status === 'used' || b.status === 'checked_out' || b.status === 'cancelled' || b.status === 'no_show' || b.status === 'expired').length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Past Bookings</h2>
          <div className="grid gap-4">
            {bookings.filter(b => b.status === 'used' || b.status === 'checked_out' || b.status === 'cancelled' || b.status === 'no_show' || b.status === 'expired').map((booking) => (
              <Card key={booking.id} className="border-slate-200 opacity-60" data-testid="past-booking-item">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-900">{booking.amenity_name}</h3>
                      <p className="text-sm text-slate-600">
                        <span className="font-medium">Date:</span> {booking.date} | <span className="font-medium">Time:</span> {booking.time_slot}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(booking.status, booking.date)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Booking Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Book {selectedAmenity?.name}</DialogTitle>
            <DialogDescription>Select your preferred date and time slot</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBooking} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={bookingData.date}
                onChange={(e) => setBookingData({ ...bookingData, date: e.target.value })}
                required
                data-testid="booking-date-input"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <Label>Time Slot</Label>
              <Select value={bookingData.time_slot} onValueChange={(value) => setBookingData({ ...bookingData, time_slot: value })}>
                <SelectTrigger data-testid="booking-timeslot-select">
                  <SelectValue placeholder="Select time slot" />
                </SelectTrigger>
                <SelectContent>
                  {selectedAmenity?.available_slots.map((slot, idx) => (
                    <SelectItem key={idx} value={slot}>{slot}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-secondary hover:bg-secondary/90" data-testid="confirm-booking-btn">Confirm Booking</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResidentAmenities;